/* eslint-disable import/extensions */
import { ContactTypeId, Countries } from '@constants/enums';
import { IOnboardingDocuments } from '@efex-services/documents/documentsSvc';
import { IRate } from '@efex-services/payments/types';
import {
  MexicoStakeHoldersFormData,
  StakeholderKYCStatus,
} from '@pages/onboarding/Mexico/types/stakeholders.type';
import { DateTime } from 'luxon';
import { StakeHolderId } from '@pages/onboarding/Mexico/constants/stakeHolderOptions';
import { Currency } from '../../components/common/Form/config';
import { Country } from '../../configs/language';
import { SnakeToCamelCase } from '../types';

export type ClientsResponse = IPagination<Result>;

export enum ContactStatus {
  PENDING = 1,
  ACTIVE = 2,
  INACTIVE = 3,
  DECLINED = 4,
}

interface Result {
  id: number;
  enterprise: Enterprise;
  contactNaturalPersonEnterprise: ContactNaturalPersonEnterprise;
  contactTypeId: number;
  enterpriseBankAccount: EnterpriseBankAccount;
  enterpriseAddress: EnterpriseAddress;
  beneficiaryRelationshipId?: number;
  contactStatus?: {
    id: ContactStatus;
    name: string;
    description: string;
  };
}

interface EnterpriseAddress {
  id: number;
  name: string;
  city: string;
  state: string;
  addressLine1: string;
  addressLine2?: string;
  extNumber?: any;
  intNumber?: any;
  postCode: string;
}

interface EnterpriseBankAccount {
  id: number;
  accountNumber: string;
  bankName: string;
  clabe: string;
  routingNumber?: any;
  accountType: string;
  accountCurrency: string;
  bankCode?: any;
  swiftCode?: any;
  countryId?: any;
  transferMethod?: any;
}

interface ContactNaturalPersonEnterprise {
  naturalPerson: NaturalPerson;
  enterprise: Enterprise2;
}

interface Enterprise2 {
  id: number;
  name: string;
  rfc: string;
  irs: string;
  website?: any;
  country: string;
  countryData: Record<string, string>;
  countryId?: any;
  account_currency?: any;
  entity_type: string;
}

interface NaturalPerson {
  uuid: string;
  firstName: string;
  lastName: string;
  nickname?: any;
  email: string;
  phoneNumber: string;
  country: string;
  countryId?: any;
  origin: string;
  documentType?: any;
  documentNumber: string;
}

interface Enterprise {
  id: number;
  name: string;
  rfc: string;
  irs: string;
  website: string;
  country: string;
  countryId?: any;
  account_currency: string;
  entity_type: string;
}

export type AccountTypes = 'BUSINESS' | 'PERSON';

export interface IPayableClient {
  country: string;
  uuid: string;
  entityBusiness: AccountTypes;
  name: string;
  companyName?: string;
  origin: 'USER' | 'CLIENT';
  currency?: string;
  bankAccount?: Bankaccount;
  contactId?: string | number;
}

export interface IBalanceChart {
  id: number;
  month: string;
  userGain: number;
  userLost: number;
}

export interface BalanceResponse {
  balance: string;
  balance_chart: IBalanceChart[];
  payable: string;
  payable_chart: IBalanceChart[];
  receivable: string;
  receivable_chart: IBalanceChart[];
}

export interface UploadFileResponse {
  idNumber: boolean;
  einDocument: boolean;
  generalDocument: boolean;
  serviceDocument: boolean;
}

export interface WalletResponse {
  id: number;
  wallet: Wallet;
  currency: WalletCurrency;
  balance: string;
  blocked: string;
  coupon?: string;
  uuid?: string;
}

export interface CombinedBalanceResponse {
  combinedBalance: number;
}

export interface WalletConversion {
  enterpriseId: string;
  currency: Currency;
  desCurrency: Currency;
  amount: string | number;
  reverse: boolean;
  rateUuid: string;
}

export interface WalletCurrency {
  id: number;
  name: string;
}

export interface Wallet {
  uuid: string;
  default_currency: string;
  wallet_type: number;
  id: number;
}

export type IWallet = {
  [Property in keyof WalletResponse as SnakeToCamelCase<Property>]: WalletResponse[Property];
};

export type ICombinedBalance = {
  [Property in keyof CombinedBalanceResponse as SnakeToCamelCase<Property>]: CombinedBalanceResponse[Property];
};

export type IBalance = {
  [Property in keyof BalanceResponse as SnakeToCamelCase<Property>]: BalanceResponse[Property];
};

// TODO: Change to cameCase
export interface GenerateTransfer {
  id?: number;
  uuid?: string;
  client_uuid: string;
  client_origin: string;
  amount: number;
  desAmount: number;
  comment: string;
  currency: Currency;
  des_currency: Currency;
  reference: string;
  invoice?: FormData;
  is_pending_transfer: boolean;
  execution_date?: string;
  country_user_origin?: string;
  country_origin?: string;
}

export enum PayableTypes {
  PROCCESS = 'PROCESSING',
  SWIFT = 'SWIFT',
  LENDING = 'LENDING',
  VALIDATION_AMOUNT = 'VALIDATION_AMOUNT',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  DEPOSITED = 'DEPOSITED',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  PENDING_TO_PROCESS = 'PENDING_TO_PROCESS',
  PENDING_TO_DEPOSIT = 'PENDING_TO_DEPOSIT',
  NEW_PAYMENT_CREATED = 'NEW_PAYMENT_CREATED',
  PENDING_TO_PROCESS_SCHEDULED_PAYMENT = 'PENDING_TO_PROCESS_SCHEDULED_PAYMENT',
  PENDING_TO_APPROVE = 'PENDING_TO_APPROVE',
  NOT_PROCESSED = 'Not_processed',
  DEPOSIT_FAILED = 'DEPOSIT_FAILED',
  BALANCE_DISCOUNTED = 'BALANCE_DISCOUNTED',
  BITSO_DEPOSIT_ERROR = 'BITSO_DEPOSIT_ERROR',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
  REFUNDED = 'REFUNDED',
  PENDING_TO_APPROVE_BY_USER = 'PENDING_TO_APPROVE_BY_USER',
  APPROVAL_TIME_EXPIRED = 'APPROVAL_TIME_EXPIRED',
}
export type PayableType = (typeof PayableTypes)[keyof typeof PayableTypes];
export type PayableTypeFilter = 'PROCESSING' | 'PENDING' | 'SWIFT';

export enum TabPayables {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  BULKS = 'BULKS',
  SWIFT = 'SWIFT',
  LENDING = 'LENDING',
}
export type TabsPayable = (typeof TabPayables)[keyof typeof TabPayables];
export type TabsPayableFilter = 'PROCESSING' | 'PENDING' | 'BULKS' | 'LENDING';

export interface IPayment {
  id: number;
  uuid: string;
  comment?: string;
  name: string;
  paymentStatus: string;
  viewStatus: string;
  date: string;
  dateLong: string;
  dueDateLong?: string;
  amount: string | number;
  desAmount: string;
  paymentsDueDate: { expired: string; status: string };
  invoiceNumber?: string | number;
  redirect?: string;
  client: IPayableClient;
  currency: Currency;
  desCurrency?: Currency;
  isPendingTransfer: boolean;
  isScheduledPayment?: boolean;
  isDeferredTransfer?: boolean;
  walletDeposit: boolean;
}

export interface IPayable extends IPayment {
  status: PayableType;
  rate?: IRate;
}

export interface IClientModal {
  id?: number | string;
  contact_np_enterprise_id?: string;
  uuid: string;
  name: string;
  country: string;
  contactId?: number;
  origin: string;
  email?: string;
  companyName: string;
  entityBusiness: string;
  currency?: Currency;
  bankName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  nickname?: string;
  bankAccount: Bankaccount;
  contactTypeId?: ContactTypeId;
  country_iso?: string;
}

export interface Bankaccount {
  id?: number;
  bank_name?: string;
  bank_address?: any;
  clabe?: string;
  routing_number?: string;
  account_number?: string;
  account_type?: string;
  currency?: any;
  swift_code?: any;
}

export interface FixedBankaccount {
  id?: number;
  bankName?: string;
  bankAddress?: any;
  clabe?: string;
  routingNumber?: string;
  accountNumber?: string;
  accountType?: string;
  currency?: any;
  swiftCode?: string;
}

export interface IClientModalResponse {
  result: IClientModal[];
  payable: IClientModal[];
}

export interface IBalancesResponse {
  origin: string;
  id: number;
  created_at: string;
  created_at_long: string;
  updated_at: string;
  is_active: boolean;
  status_bitso: string;
  status_plaid: string;
  status_wyre: string;
  type: string;
  amount: string;
  deposit_amount: string;
  deposit_status: string;
  transfer_id: string;
  transfer_json: string;
  transfer_instructions: string;
  uuid: string;
  user: string;
  currency: string;
}

export interface PaymentItemDetail {
  name: string;
  quantity: string;
  price: number;
}

export interface PaymentDetailResponse {
  amount: number;
  des_amount: number;
  rate: number;
  des_comment: string;
  address: string;
  city_state: string;
  country: Country;
  currency: Currency;
  des_currency: Currency;
  origin_created: string;
  date: string;
  due_date: string;
  due_date_long: string;
  updated_at: string;
  from: string;
  invoice_files?: string[] | null;
  items: PaymentItemDetail[];
  to: string;
  rejected_reason?: string;
  comment?: string;
  clabe?: string;
  routing_number?: string;
  account_number?: string;
  payment_status?: string;
  fee?: string;
  beneficiary?: string;
  url?: string;
  cep?: string;
}

export type IPaymentDetail = {
  [Property in keyof PaymentDetailResponse as SnakeToCamelCase<Property>]: PaymentDetailResponse[Property];
};

export type IBalanceFunds = {
  [Property in keyof IBalancesResponse as SnakeToCamelCase<Property>]: IBalancesResponse[Property];
};

export interface IValidateContactEmailCamelCase {
  id: number;
  enterprise: Enterprise;
  naturalPerson: NaturalPerson;
  accounts: Account[];
  addresses: Address[];
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

interface Address {
  id: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  name: string;
  city: string;
  state: string;
  street: string;
  extNumber?: any;
  intNumber?: any;
  postCode: string;
  addressLine1: string;
  addressLine2?: any;
  enterprise: number;
}

interface Account {
  id: number;
  bankName?: string;
  clabe?: string;
  taxId?: string;
  routingNumber?: string;
  accountNumber?: string;
}

interface NaturalPerson {
  id: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  uuid: string;
  address?: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string;
  managerEmail?: any;
  country: string;
  dateOfBirth: string;
  documentNumber: string;
  shareholder: boolean;
  gender?: any;
  origin: string;
  nickName?: string;
}

interface Enterprise {
  id: number;
  name: string;
  entityType: string;
  country: string;
}

export interface IPaymentBalanceItem {
  month: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  currency: Currency;
  amount: number;
  conversion?: number;
}

export interface IPaymentsBalanceResponse {
  default_currency: Currency;
  receivables: IPaymentBalanceItem[];
  payables: IPaymentBalanceItem[];
  receivable_amount: number;
  payable_amount: number;
}

export type IPaymentsBalance = {
  [Property in keyof IPaymentsBalanceResponse as SnakeToCamelCase<Property>]: IPaymentsBalanceResponse[Property];
};

export interface PayableResponse {
  id: number;
  client: PayableClient;
  currency: string;
  alert_sign: string;
  due_date: string;
  due_date_long: string;
  created_at: string;
  invoice: string;
  created_at_long: string;
  updated_at: string;
  is_active: boolean;
  uuid: string;
  status: string;
  view_status: string;
  amount: string;
  des_amount: string;
  comment?: string;
  taxes: string;
  city_state?: any;
  address?: any;
  country?: any;
  exchange_reference?: string;
  origin: string;
  es_cow: boolean;
  invoice_number: string;
  transfer_origin?: string;
  transfer_id?: string;
  transfer_json?: string;
  transfer_response?: any;
  phone_pin?: any;
  is_pending_admin_process: boolean;
  is_pending_transfer: boolean;
  is_deferred_transfer: boolean;
  payment_status: string;
  payment_method: string;
  wallet_deposit: boolean;
  natural_person?: number;
  user: number;
  receivable: number;
}

export interface PayableClient {
  id: number;
  user: string;
  company_name: string;
  email: string;
  clabe?: string;
  account_number: string;
  routing_number: string;
  phone_number: string;
  register_date: string;
  created_at: string;
  uuid: string;
  origin: string;
  natural_person: string;
  updated_at: string;
  is_active: boolean;
  type_business: string;
  entity_business: string;
  name: string;
  country: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postCode?: string;
  tax_id?: string;
  rfc?: string;
  currency?: string;
  bank_code?: any;
  account_type?: string;
  identification_number: string;
  document_type?: any;
  circle_id?: string;
  circle_response?: string;
  bank_name: string;
  bank_city?: string;
  bank_address?: string;
  bank_district?: string;
  is_showed: boolean;
  bankAccount?: FixedBankaccount;
}

export type IPayableNew = {
  [Property in keyof PayableResponse as SnakeToCamelCase<Property>]: PayableResponse[Property];
};

export interface IPagination<T> {
  pageSize: number;
  page_size?: number;
  totalObjects: number;
  total_objects: number;
  totalPages: number;
  total_pages: number;
  currentPageNumber: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type IPayablePagination = IPagination<IPayable>;
export type IClientsPagination = IPagination<Result>;

export interface IOnboardingFormData {
  countryOrigin: Countries;
  accountNumber: string;
  accountType: string;
  addressLine: string;
  addressLine2?: string;
  bankCode: string;
  bankName: string;
  bankStatement: File;
  certificateFile: File;
  constitutionFile: File;
  incorporation: DateTime;
  phoneNumber?: string;
  date?: DateTime;
  phoneNumberEnterprise: string;
  taxId: string;
  idNumber: string;
  website: string;
  isLastStep: boolean;
  isFirstAccount?: boolean;
  postCode: string;
  postalCodeId?: string;
}

export interface IUploadDocumentsOnboarding
  extends Omit<
    IOnboardingFormData,
    'incorporation' | 'isLastStep' | 'date' | 'addressLine'
  > {
  incorporation: string;
  date: string;
  completed?: boolean;
}

export interface IRawDocumentsOnboarding extends IOnboardingFormData {
  stakeholders: StakeHoldersFormData[];
  isStakeHolderDataConfirm?: boolean;
}

export interface StakeHoldersFormData extends MexicoStakeHoldersFormData {
  id?: number;
  name: string;
  lastName: string;
  birthDate: DateTime;
  percentage: string;
  title: string;
  idType: string;
  idNumber: string;
  idFileFront: File | undefined;
  idFileBack: File | undefined;
  addressLine1: string;
  residenceFile: File | undefined;
  addressLine2?: string;
  city: string;
  state: string;
  postCode: string;
  postalCodeId?: number;
  isIdFileFront?: string;
  isIdFileBack?: string;
  isResidenceFile?: string;
  dataConfirm?: boolean;
  expiredAt?: string;
  effectiveAt?: string;
  neighborhood?: string;
  documents?: IOnboardingDocuments[];
  legalRepresentativeStatament: boolean;
  legalRepresentativeResponse?: string;
  resourceFamilyStatament: boolean;
  resourceFamilyResponse?: string;
  proofOfAddress?: File | null | IOnboardingDocuments | IOnboardingDocuments[];
  passport?: File | null | IOnboardingDocuments;
  personalIdentification?: File | null | IOnboardingDocuments;
  personalIdentificationBack?: File | null | IOnboardingDocuments;
  ssn?: string;
  kycStatus: StakeholderKYCStatus;
}

export interface UploadStaStakeHolders
  extends Omit<StakeHoldersFormData, 'birthDate' | 'id'> {
  birthDate?: string;
  id?: number | string;
  entityTypeId?: StakeHolderId;
}

export interface MexicoOnboardingGet {
  data: MexicoOnboardingGetData;
}

export interface MexicoOnboardingGetData {
  idNumber: boolean;
  certificate: boolean;
  bankStatement: boolean;
  statements: Statement2[];
  companySector: CompanySector;
  bankName?: any;
  bankCode?: any;
  accountNumber?: any;
  routingNumber?: any;
  documentType?: any;
  accountType?: any;
  taxId: string;
  rfc?: string;
  accountCurrency: string;
  birthDate: string;
  incorporation: string;
  website?: any;
  phoneNumber: string;
  phoneNumberEnterprise: string;
  addressLine1: string;
  addressLine2: string;
  postCode: number;
  isStakeHolderDataConfirm: boolean;
  city?: string;
  state?: string;
  idType?: string;
}

export interface CompanySector {
  id: number;
  economicActivityKey: number;
  economicActivity: string;
  classification: string;
}

export interface Statement2 {
  statement: Statement;
  value: string;
}

export interface Statement {
  id: number;
  name: string;
}

export type GetContactCurrencyResponse = {
  iso_code: string;
  name: string;
};

export type GetContactCurrencyRequest = {
  enterpriseId: string;
  contactNpEnterpriseId: string;
};

export interface ICreateStakeholderV2 {
  enterpriseID: string;
  values: ICreateStakeholderpayload;
}
export interface ICreateStakeholderpayload {
  name: string;
  lastName: string;
  secondLastName: string;
  percentage: string;
  title: string;
  gender?: 'F' | 'M' | undefined;
  curp?: string;
  birthDate?: string;
  nationalityCountryId?: string;
  federalEntityId?: string;
  documentIds: number[];
  address: ICreateStakeholderV2Address;
  legalRepresentativeStatament?: boolean;
  legalRepresentativeResponse?: string;
  resourceFamilyStatament?: boolean;
  resourceFamilyResponse?: string;
  entityTypeId?: StakeHolderId;
  ssn?: string;
}

interface ICreateStakeholderV2Address {
  addressLine1: string;
  addressLine2?: string;
  neighborhood?: string;
  city: string;
  state: string;
  postCode: string;
  postalCodeId?: number;
}
